import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { formatIDR } from "@/lib/month";
import { Calculator, Loader2, Plus, Trash2, Save, Percent, Banknote, Landmark, CreditCard, Tag, Anchor, ArrowRight } from "lucide-react";

const CATEGORIES = ["genius", "targeting", "campaign", "portfolio", "deep", "mega", "seasonal", "essential", "targeted", "promo", "base", "custom"];

// "base" layers (e.g. Tiket Exclusive Private Deals) are % of BAR, subtracted after compounding
const isBase = (l) => l.category === "base";

// B.com stacking scheme: per category only the LARGEST discount applies; deep deals never stack
// (applied to base rate); campaign stacks with genius only; targeting/portfolio stack with genius
// (+ each other); the combo producing the lowest price wins. "custom" layers always stack.
export function effectiveLayers(layers) {
  const active = (layers || []).filter((l) => l.active !== false && (Number(l.pct) || 0) > 0);
  const deep = active.filter((l) => l.category === "deep");
  if (deep.length) return [deep.reduce((a, b) => (Number(b.pct) > Number(a.pct) ? b : a))];
  const customs = active.filter((l) => !l.category || l.category === "custom" || isBase(l));
  const byCat = {};
  active.forEach((l) => {
    if (!l.category || l.category === "custom" || isBase(l)) return;
    if (!byCat[l.category] || Number(l.pct) > Number(byCat[l.category].pct)) byCat[l.category] = l;
  });
  // "promo" (Airbnb group: new listing / custom promo / weekly / monthly / early-bird / last-minute)
  // is mutually exclusive — only the largest applies — and stacks with custom layers (non-refundable, TRGD, MOD)
  const combo1 = [byCat.genius, byCat.targeting, byCat.portfolio, byCat.promo].filter(Boolean);
  const combo2 = [byCat.genius, byCat.campaign, byCat.promo].filter(Boolean);
  const factor = (ls) => ls.reduce((f, l) => f * (1 - Number(l.pct) / 100), 1);
  const best = factor(combo1) <= factor(combo2) ? combo1 : combo2;
  return [...customs, ...best];
}

// compound factor honoring stacking rules; base layers subtract % of BAR at the end
const compoundFactor = (layers) => {
  const eff = effectiveLayers(layers);
  const f = eff.filter((l) => !isBase(l)).reduce((x, l) => x * (1 - Number(l.pct) / 100), 1);
  const b = eff.filter(isBase).reduce((s, l) => s + Number(l.pct), 0) / 100;
  return Math.max(0, f - b);
};
const afterLayers = (bar, layers) => bar * compoundFactor(layers);

// Agoda-style additive stacking (penambahan): percentages simply add up (mobile 10% + member 6% = 16% off base)
const activeLayersOf = (ch) => (ch.layers || []).filter((l) => l.active !== false && (Number(l.pct) || 0) > 0);
const priceAfter = (bar, ch) => {
  if (ch.stacking === "additive") {
    const total = activeLayersOf(ch).reduce((s, l) => s + Number(l.pct), 0);
    return bar * Math.max(0, 1 - total / 100);
  }
  return afterLayers(bar, ch.layers);
};
const factorOf = (ch) => {
  if (ch.stacking === "additive") return Math.max(0, 1 - activeLayersOf(ch).reduce((s, l) => s + Number(l.pct), 0) / 100);
  return compoundFactor(ch.layers);
};

const fees = (price, ch) => {
  const comm = price * (Number(ch.commission_pct) || 0) / 100;
  // tax & bank fee are charged on the OTA commission amount, not on the sell price
  const tax = comm * (Number(ch.tax_pct) || 0) / 100;
  const bank = comm * (Number(ch.bank_pct) || 0) / 100;
  return { comm, tax, bank, net: price - comm - tax - bank };
};

function PctInput({ value, onChange, testId, dark }) {
  return (
    <div className="relative">
      <Input
        type="number"
        min="0"
        step="0.5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-8 text-sm pr-7 ${dark ? "bg-slate-800/60 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900"}`}
        data-testid={testId}
      />
      <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>%</span>
    </div>
  );
}

function ChannelCard({ ch, bar, anchor, targetGuest, mode, targetNet, dark, isAdmin, onChange, onSave, onDelete, saving }) {
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const inputCls = dark ? "bg-slate-800/60 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900";
  const upd = (k, v) => onChange(ch.id, { ...ch, [k]: v });
  const updLayer = (lid, k, v) => onChange(ch.id, { ...ch, layers: ch.layers.map((l) => (l.id === lid ? { ...l, [k]: v } : l)) });
  const addLayer = () => onChange(ch.id, { ...ch, layers: [...ch.layers, { id: `l-${Date.now()}`, name: "New promo", pct: 0, category: "custom", active: true }] });
  const removeLayer = (lid) => onChange(ch.id, { ...ch, layers: ch.layers.filter((l) => l.id !== lid) });

  const isAdditive = ch.stacking === "additive";
  const applied = isAdditive ? activeLayersOf(ch) : effectiveLayers(ch.layers);
  const fixed = priceAfter(bar, ch);

  // parity: extra promo needed so guest price matches the anchor (B.com)
  // additive: extra % applies on BAR; compound: extra % applies on post-layer price
  const requiredExtra = !anchor && targetGuest > 0 && fixed > targetGuest
    ? (isAdditive ? ((fixed - targetGuest) / bar) * 100 : (1 - targetGuest / fixed) * 100)
    : 0;
  const finalGuest = anchor ? fixed : (isAdditive ? fixed - (bar * requiredExtra) / 100 : fixed * (1 - requiredExtra / 100));
  const f = fees(finalGuest, ch);
  const totalDisc = bar > 0 ? (1 - finalGuest / bar) * 100 : 0;

  // reverse mode: target NET -> required sell price -> required BAR given active layers
  // net = price * (1 - c * (1 + t + b)) since tax & bank are % of the commission
  const cRate = (Number(ch.commission_pct) || 0) / 100;
  const feeRate = cRate * (1 + (Number(ch.tax_pct) || 0) / 100 + (Number(ch.bank_pct) || 0) / 100);
  const reqSell = targetNet > 0 && feeRate < 1 ? targetNet / (1 - feeRate) : 0;
  const layerFactor = factorOf(ch);
  const reqBar = reqSell > 0 && layerFactor > 0 ? reqSell / layerFactor : null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border p-5 shadow-sm ${
        anchor
          ? dark ? "border-amber-600/50 bg-gradient-to-b from-amber-950/30 to-slate-900/90" : "border-amber-300 bg-gradient-to-b from-amber-50 to-white"
          : dark ? "border-slate-800 bg-slate-900/90" : "border-slate-200 bg-white"
      }`}
      data-testid={`rate-channel-${ch.id}`}>
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {anchor && <Anchor className={`h-4 w-4 shrink-0 ${dark ? "text-amber-400" : "text-amber-600"}`} />}
          <Input
            value={ch.name}
            onChange={(e) => upd("name", e.target.value)}
            className={`bg-transparent border-transparent text-lg font-display font-semibold h-9 w-auto max-w-[190px] px-2 -ml-2 ${dark ? "text-white hover:border-slate-700 focus:border-slate-600" : "text-slate-900 hover:border-slate-300 focus:border-slate-400"}`}
            data-testid={`channel-name-${ch.id}`}
          />
          {anchor && <Badge className={`text-[10px] border ${dark ? "bg-amber-500/15 text-amber-300 border-amber-700/40" : "bg-amber-100 text-amber-700 border-amber-300"}`}>ANCHOR</Badge>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="outline" className={`h-8 ${dark ? "bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" : ""}`} onClick={() => onSave(ch)} disabled={saving} data-testid={`save-channel-${ch.id}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          </Button>
          {!ch.is_default && isAdmin && (
            <Button size="sm" variant="outline" className={`h-8 ${dark ? "bg-transparent border-rose-900/60 text-rose-400 hover:bg-slate-800" : "border-rose-200 text-rose-600"}`} onClick={() => onDelete(ch.id)} data-testid={`delete-channel-${ch.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2.5 mb-4">
        <div className="space-y-1">
          <Label className={`text-[11px] ${sub} flex items-center gap-1`}><Percent className="h-3 w-3" /> Komisi OTA</Label>
          <PctInput value={ch.commission_pct} onChange={(v) => upd("commission_pct", v)} testId={`commission-${ch.id}`} dark={dark} />
        </div>
        <div className="space-y-1">
          <Label className={`text-[11px] ${sub} flex items-center gap-1`}><Landmark className="h-3 w-3" /> Tax</Label>
          <PctInput value={ch.tax_pct} onChange={(v) => upd("tax_pct", v)} testId={`tax-${ch.id}`} dark={dark} />
        </div>
        <div className="space-y-1">
          <Label className={`text-[11px] ${sub} flex items-center gap-1`}><CreditCard className="h-3 w-3" /> Bank fee</Label>
          <PctInput value={ch.bank_pct} onChange={(v) => upd("bank_pct", v)} testId={`bank-${ch.id}`} dark={dark} />
        </div>
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between">
          <Label className={`text-[11px] ${sub} flex items-center gap-1`}><Tag className="h-3 w-3" /> {anchor ? "Diskon B.com (toggle untuk simulasi)" : "Promo yang sudah aktif"}</Label>
          <div className="flex items-center gap-2">
            <select
              value={ch.stacking || "compound"}
              onChange={(e) => upd("stacking", e.target.value)}
              className={`h-6 rounded border text-[10px] px-1 ${dark ? "bg-slate-800/60 border-slate-700 text-slate-300" : "bg-white border-slate-300 text-slate-600"}`}
              data-testid={`stacking-${ch.id}`}
              title="Cara stack diskon: bertingkat (compound) atau penambahan (additive)"
            >
              <option value="compound">Bertingkat ×</option>
              <option value="additive">Penambahan +</option>
            </select>
            <button onClick={addLayer} className={`text-[11px] flex items-center gap-1 ${dark ? "text-indigo-400 hover:text-indigo-300" : "text-indigo-600 hover:text-indigo-500"}`} data-testid={`add-layer-${ch.id}`}>
              <Plus className="h-3 w-3" /> layer
            </button>
          </div>
        </div>
        {(ch.layers || []).map((l) => (
          <div key={l.id} className={`flex items-center gap-2 ${l.active === false ? "opacity-45" : ""}`} data-testid={`layer-${ch.id}-${l.id}`}>
            <Switch checked={l.active !== false} onCheckedChange={(v) => updLayer(l.id, "active", v)} data-testid={`layer-toggle-${ch.id}-${l.id}`} />
            <Input value={l.name} onChange={(e) => updLayer(l.id, "name", e.target.value)} className={`h-8 text-sm flex-1 min-w-0 ${inputCls}`} data-testid={`layer-name-${ch.id}-${l.id}`} />
            <select
              value={l.category || "custom"}
              onChange={(e) => updLayer(l.id, "category", e.target.value)}
              className={`h-8 rounded-md border text-[11px] px-1.5 w-[92px] ${dark ? "bg-slate-800/60 border-slate-700 text-slate-300" : "bg-white border-slate-300 text-slate-600"}`}
              data-testid={`layer-cat-${ch.id}-${l.id}`}
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="w-20 shrink-0"><PctInput value={l.pct} onChange={(v) => updLayer(l.id, "pct", v)} testId={`layer-pct-${ch.id}-${l.id}`} dark={dark} /></div>
            <button onClick={() => removeLayer(l.id)} className={`shrink-0 ${dark ? "text-slate-600 hover:text-rose-400" : "text-slate-400 hover:text-rose-500"}`} data-testid={`remove-layer-${ch.id}-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {(ch.layers || []).length === 0 && <p className={`text-[11px] italic ${sub}`}>Belum ada promo aktif — harga publish = harga dasar.</p>}
        {(ch.layers || []).length > 0 && (
          <p className={`text-[11px] ${sub}`} data-testid={`applied-${ch.id}`}>
            Berlaku: {applied.length ? applied.map((l) => `${l.name} ${l.pct}%`).join(isAdditive ? " + " : " → ") : "tidak ada"}
            {isAdditive && applied.length > 0 && <span className={dark ? "text-teal-400" : "text-teal-600"}> = {applied.reduce((s, l) => s + Number(l.pct), 0)}% (penambahan)</span>}
            {!isAdditive && applied.some(isBase) && <span className={dark ? "text-teal-400" : "text-teal-600"}> (layer base dihitung dari BAR)</span>}
            {!isAdditive && applied.some((l) => l.category === "deep") && <span className={dark ? "text-amber-400" : "text-amber-600"}> (deep deal tidak stack)</span>}
            {!isAdditive && activeLayersOf(ch).filter((l) => l.category === "promo").length > 1 && <span className={dark ? "text-sky-400" : "text-sky-600"}> (grup promo Airbnb: hanya 1 yang terbesar berlaku)</span>}
          </p>
        )}
      </div>

      {/* Parity result (forward mode) */}
      {!anchor && mode === "forward" && (
        <div className={`rounded-xl border p-3.5 mb-3 ${dark ? "border-indigo-700/50 bg-indigo-950/40" : "border-indigo-200 bg-indigo-50"}`} data-testid={`parity-${ch.id}`}>
          {requiredExtra > 0 ? (
            <div className="flex items-center justify-between gap-2">
              <span className={`text-xs flex items-center gap-1.5 ${dark ? "text-slate-300" : "text-slate-600"}`}><ArrowRight className={`h-3.5 w-3.5 ${dark ? "text-indigo-400" : "text-indigo-500"}`} /> {isAdditive ? `Promo tambahan di ${ch.name} (penambahan)` : `Promo yang harus dibuat di ${ch.name}`}</span>
              <span className={`font-display font-bold text-2xl ${dark ? "text-indigo-300" : "text-indigo-600"}`} data-testid={`required-promo-${ch.id}`}>{requiredExtra.toFixed(1)}%</span>
            </div>
          ) : targetGuest > 0 && fixed <= targetGuest ? (
            <p className={`text-xs ${dark ? "text-amber-300" : "text-amber-700"}`} data-testid={`parity-note-${ch.id}`}>Promo aktif sudah melebihi B.com — harga tamu {formatIDR(Math.round(fixed))} lebih murah dari target {formatIDR(Math.round(targetGuest))}. Kurangi layer kalau mau parity.</p>
          ) : (
            <p className={`text-xs ${sub}`}>Isi BAR & promo B.com dulu untuk melihat promo yang dibutuhkan.</p>
          )}
        </div>
      )}

      {/* Target NET mode: required BAR per channel */}
      {mode === "target" && (
        <div className={`rounded-xl border p-3.5 mb-3 ${dark ? "border-teal-700/50 bg-teal-950/40" : "border-teal-200 bg-teal-50"}`} data-testid={`targetmode-${ch.id}`}>
          {reqBar ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className={`text-xs ${dark ? "text-slate-300" : "text-slate-600"}`}>BAR minimum di {ch.name}</span>
                <span className={`font-display font-bold text-2xl ${dark ? "text-teal-300" : "text-teal-700"}`} data-testid={`required-bar-${ch.id}`}>{formatIDR(Math.ceil(reqBar))}</span>
              </div>
              <p className={`text-[11px] mt-1 ${sub}`}>Harga tamu {formatIDR(Math.round(reqSell))} − fee {(feeRate * 100).toFixed(1)}% ≈ NET {formatIDR(Math.round(targetNet))}</p>
            </>
          ) : (
            <p className={`text-xs ${sub}`}>Isi target NET dulu.</p>
          )}
        </div>
      )}

      <div className={`rounded-xl border p-3.5 space-y-1.5 text-sm ${dark ? "bg-slate-800/40 border-slate-800" : "bg-slate-50 border-slate-200"}`}>
        <div className={`flex justify-between ${dark ? "text-slate-300" : "text-slate-700"}`}>
          <span>Harga tamu {anchor ? "di B.com" : `di ${ch.name}`} <span className={`text-[10px] ${sub}`}>(BAR − {totalDisc.toFixed(1)}%)</span></span>
          <span className={`font-mono font-semibold ${dark ? "text-white" : "text-slate-900"}`} data-testid={`sell-${ch.id}`}>{formatIDR(Math.round(finalGuest))}</span>
        </div>
        {!anchor && requiredExtra > 0 && (
          <div className={`flex justify-between text-[11px] ${dark ? "text-indigo-300/80" : "text-indigo-500"}`}>
            <span>Parity dengan B.com</span>
            <span>{formatIDR(Math.round(finalGuest))} ≈ {formatIDR(Math.round(targetGuest))} ✓</span>
          </div>
        )}
        <div className={`flex justify-between text-xs ${sub}`}>
          <span>Komisi OTA ({ch.commission_pct || 0}%)</span>
          <span className={`font-mono ${dark ? "text-rose-400" : "text-rose-600"}`}>− {formatIDR(Math.round(f.comm))}</span>
        </div>
        <div className={`flex justify-between text-xs ${sub}`}>
          <span>Tax ({ch.tax_pct || 0}% dr komisi)</span>
          <span className={`font-mono ${dark ? "text-rose-400" : "text-rose-600"}`}>− {formatIDR(Math.round(f.tax))}</span>
        </div>
        <div className={`flex justify-between text-xs ${sub}`}>
          <span>Bank fee ({ch.bank_pct || 0}% dr komisi)</span>
          <span className={`font-mono ${dark ? "text-rose-400" : "text-rose-600"}`}>− {formatIDR(Math.round(f.bank))}</span>
        </div>
        <div className={`flex justify-between items-center pt-2 mt-1 border-t ${dark ? "border-slate-700" : "border-slate-200"}`}>
          <span className={`font-semibold flex items-center gap-1.5 ${dark ? "text-white" : "text-slate-900"}`}><Banknote className={`h-4 w-4 ${dark ? "text-emerald-400" : "text-emerald-600"}`} /> NET diterima</span>
          <div className="text-right">
            <div className={`font-mono font-bold text-lg ${dark ? "text-emerald-400" : "text-emerald-600"}`} data-testid={`net-${ch.id}`}>{formatIDR(Math.round(f.net))}</div>
            <Badge variant="outline" className={`text-[10px] ${dark ? "border-emerald-700/50 text-emerald-300" : "border-emerald-300 text-emerald-700"}`}>{bar > 0 ? ((f.net / bar) * 100).toFixed(1) : 0}% dari BAR</Badge>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function RateSimulatorPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dark = isAdmin;
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const [drafts, setDrafts] = useState({});
  const [bar, setBar] = useState(500000);
  const [mode, setMode] = useState("forward");
  const [targetNet, setTargetNet] = useState(400000);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rate-simulator/channels");
      const d = {};
      data.channels.forEach((c) => { d[c.id] = c; });
      setDrafts(d);
    } catch { toast.error("Failed to load channels"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = Object.values(drafts);
  const anchorCh = list.find((c) => c.is_anchor) || list.find((c) => /booking/i.test(c.name)) || list[0];
  const targetGuest = anchorCh ? priceAfter(bar, anchorCh) : 0;

  const onChange = (id, cfg) => setDrafts((d) => ({ ...d, [id]: cfg }));

  const save = async (ch) => {
    setSavingId(ch.id);
    try {
      await api.put(`/rate-simulator/channels/${ch.id}`, {
        name: ch.name,
        stacking: ch.stacking || "compound",
        commission_pct: Number(ch.commission_pct) || 0,
        tax_pct: Number(ch.tax_pct) || 0,
        bank_pct: Number(ch.bank_pct) || 0,
        layers: ch.layers.map((l) => ({ id: l.id, name: l.name, pct: Number(l.pct) || 0, category: l.category || "custom", active: l.active !== false })),
      });
      toast.success(`${ch.name} preset disimpan`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const addChannel = async () => {
    if (!newName.trim()) return toast.error("Isi nama channel dulu");
    try {
      await api.post("/rate-simulator/channels", { name: newName.trim(), commission_pct: 15, tax_pct: 11, bank_pct: 0, layers: [] });
      toast.success("Channel ditambahkan");
      setNewName("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/rate-simulator/channels/${id}`); toast.success("Channel dihapus"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className={`min-h-screen ${dark ? "bg-[#14110F] dark-scroll text-slate-100" : "bg-[#FAF7F2] text-slate-900"}`}>
      <AppNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="rate-simulator-page">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className={`font-display text-2xl font-bold flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}><Calculator className={`h-6 w-6 ${dark ? "text-amber-400" : "text-amber-600"}`} /> Rate Simulator</h1>
            <p className={`text-sm ${sub}`}>Booking.com jadi acuan (anchor) dengan skema stacking resmi — simulator menghitung promo yang harus dibuat di OTA lain supaya harga tamu parity, lengkap dengan NET per channel</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nama OTA baru…" className={`w-44 h-9 ${dark ? "bg-slate-900 border-slate-700 text-slate-200" : "bg-white border-slate-300"}`} data-testid="new-channel-name" />
              <Button variant="outline" onClick={addChannel} className={`h-9 ${dark ? "bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" : ""}`} data-testid="add-channel-btn"><Plus className="h-4 w-4" /> Channel</Button>
            </div>
          )}
        </div>

        <div className={`rounded-2xl border p-5 mb-6 flex items-center gap-5 flex-wrap ${dark ? "border-amber-800/40 bg-gradient-to-r from-slate-900 to-slate-900/60" : "border-amber-200 bg-white"}`} data-testid="bar-input-card">
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${dark ? "bg-amber-500/15" : "bg-amber-100"}`}><Banknote className={`h-6 w-6 ${dark ? "text-amber-400" : "text-amber-600"}`} /></div>
          <div className={`flex rounded-lg border overflow-hidden self-center ${dark ? "border-slate-700" : "border-slate-300"}`} data-testid="mode-toggle">
            <button onClick={() => setMode("forward")} className={`px-3.5 h-9 text-xs font-medium ${mode === "forward" ? (dark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}`} data-testid="mode-forward">BAR → NET</button>
            <button onClick={() => setMode("target")} className={`px-3.5 h-9 text-xs font-medium border-l ${dark ? "border-slate-700" : "border-slate-300"} ${mode === "target" ? (dark ? "bg-teal-500/20 text-teal-300" : "bg-teal-100 text-teal-700") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}`} data-testid="mode-target">Target NET → BAR</button>
          </div>
          {mode === "forward" ? (
            <div className="flex-1 min-w-[220px]">
              <Label className={`text-xs ${sub}`}>Harga publish / BAR (per malam, sama di semua OTA)</Label>
              <div className="relative mt-1 max-w-xs">
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${sub}`}>Rp</span>
                <Input type="number" min="0" value={bar} onChange={(e) => setBar(Number(e.target.value) || 0)} className={`h-11 text-lg font-mono font-semibold pl-10 ${dark ? "bg-slate-800/60 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}`} data-testid="bar-input" />
              </div>
            </div>
          ) : (
            <div className="flex-1 min-w-[220px]">
              <Label className={`text-xs ${sub}`}>Target NET yang mau diterima (per malam)</Label>
              <div className="relative mt-1 max-w-xs">
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${sub}`}>Rp</span>
                <Input type="number" min="0" value={targetNet} onChange={(e) => setTargetNet(Number(e.target.value) || 0)} className={`h-11 text-lg font-mono font-semibold pl-10 ${dark ? "bg-slate-800/60 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}`} data-testid="target-net-input" />
              </div>
            </div>
          )}
          {mode === "forward" && anchorCh && (
            <div className={`rounded-xl border px-4 py-2.5 ${dark ? "bg-black/30 border-amber-800/40" : "bg-amber-50 border-amber-200"}`}>
              <div className={`text-[11px] ${dark ? "text-amber-200/70" : "text-amber-700"}`}>Harga tamu target (B.com)</div>
              <div className={`font-mono font-bold text-xl ${dark ? "text-amber-300" : "text-amber-700"}`} data-testid="target-guest">{formatIDR(Math.round(targetGuest))}</div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-amber-400" /></div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {list.map((ch) => (
              <ChannelCard
                key={ch.id}
                ch={ch}
                bar={bar}
                anchor={anchorCh && ch.id === anchorCh.id}
                targetGuest={targetGuest}
                mode={mode}
                targetNet={targetNet}
                dark={dark}
                isAdmin={isAdmin}
                onChange={onChange}
                onSave={save}
                onDelete={del}
                saving={savingId === ch.id}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
